import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, BedDouble, FileText, Sparkles, Headset,
  Users, Plane, Briefcase, ShieldCheck, CheckCircle2, Building2,
} from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/motion";
import { Container, SectionHeading } from "@/components/Section";
import LeadForm from "@/components/LeadForm";
import { IMAGES } from "@/data/content";

const BENEFITS = [
  {
    icon: BedDouble,
    t: "Ready to live, from day one",
    d: "Professionally prepared Dublin apartments and houses — furnished, connected and set up for stays of 21 days or more.",
  },
  {
    icon: FileText,
    t: "One agreement, monthly invoicing",
    d: "A single point of billing instead of juggling bookings, deposits and receipts across platforms.",
  },
  {
    icon: Sparkles,
    t: "Housekeeping & linen handled",
    d: "Scheduled professional cleans, fresh linen and restocked essentials throughout the stay.",
  },
  {
    icon: Headset,
    t: "Local support, one contact",
    d: "A real person in Dublin looking after your team — check-ins, questions and anything that comes up mid-stay.",
  },
];

const WHO_BOOKS = [
  { icon: Briefcase, t: "Project teams", d: "Multi-week engagements that need more than a hotel room." },
  { icon: Plane, t: "Relocations", d: "A soft landing for staff moving to Dublin, while they find their feet." },
  { icon: Users, t: "Executives & consultants", d: "Comfortable, well-located bases for recurring visits." },
  { icon: ShieldCheck, t: "Insurance & between-homes stays", d: "Dependable medium-term accommodation when plans change." },
];

const INCLUDED = [
  "Fully furnished, guest-ready properties",
  "Utilities, Wi-Fi and setup included",
  "Scheduled housekeeping and linen service",
  "Check-in coordination and local welcome",
  "Maintenance handled during the stay",
  "Single monthly invoice for your finance team",
];

export default function CorporateStays() {
  return (
    <main data-testid="corporate-stays-page">
      <section className="noise-dark relative overflow-hidden bg-forest pb-24 pt-44 text-cream sm:pb-32 sm:pt-52">
        <div className="pointer-events-none absolute -top-40 -left-40 h-[480px] w-[480px] rounded-full bg-forest-700/30 blur-[140px]" />
        <Container className="relative z-10 grid items-center gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <p className="eyebrow flex items-center gap-2.5 text-sage">
                <Building2 className="h-4 w-4" /> Corporate stays · 21+ days
              </p>
            </FadeUp>
            <MaskedLines
              className="mt-6"
              lineClassName="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.06]"
              lines={[
                <span key="1">A better base for</span>,
                <span key="2" className="italic text-sage-light">your people in Dublin.</span>,
              ]}
            />
            <FadeUp delay={0.35}>
              <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-cream/70">
                FieldGreen provides fully managed corporate accommodation for stays of 21 days or more —
                for project teams, relocations and executives who need a well-run Dublin base,
                not another hotel booking.
              </p>
            </FadeUp>
            <FadeUp delay={0.5}>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
                <a
                  href="#corporate-enquiry"
                  data-testid="corporate-hero-cta"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage"
                >
                  Tell Us What Your Team Needs
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <Link
                  to="/assessment"
                  data-testid="corporate-owner-cta"
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-cream/25 px-7 py-4 text-sm font-semibold text-cream transition-colors duration-300 hover:border-sage hover:text-sage-light"
                >
                  Own a property? Host corporate stays
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.25} className="lg:col-span-5">
            <div className="overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.45)]">
              <img
                src={IMAGES.livingRoom}
                alt="A premium Dublin apartment set up for a corporate stay"
                className="h-80 w-full object-cover lg:h-[440px]"
                data-testid="corporate-hero-image"
              />
            </div>
          </FadeUp>
        </Container>
      </section>

      <section className="bg-cream py-28 sm:py-36">
        <Container>
          <SectionHeading
            index="01"
            eyebrow="Why companies choose FieldGreen"
            title="Corporate accommodation, minus the admin."
          />
          <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-forest/10 bg-forest/10 sm:grid-cols-2">
            {BENEFITS.map(({ icon: Icon, t, d }, i) => (
              <FadeUp key={t} delay={i * 0.06} className="h-full">
                <div
                  data-testid={`corporate-benefit-${i}`}
                  className="group flex h-full flex-col bg-cream p-9 transition-colors duration-500 hover:bg-forest"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-forest/15 text-forest-700 transition-colors duration-500 group-hover:border-sage/30 group-hover:text-sage">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-7 font-serif text-2xl font-medium text-forest transition-colors duration-500 group-hover:text-cream">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/65 transition-colors duration-500 group-hover:text-cream/65">{d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-bone py-28 sm:py-36">
        <Container>
          <SectionHeading
            index="02"
            eyebrow="Who books with us"
            title="Built for stays measured in weeks, not nights."
          />
          <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {WHO_BOOKS.map(({ icon: Icon, t, d }, i) => (
              <FadeUp key={t} delay={i * 0.07} className="h-full">
                <div
                  data-testid={`corporate-who-${i}`}
                  className="flex h-full flex-col rounded-2xl border border-forest/12 bg-cream p-8 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(15,38,28,0.12)]"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest text-sage">
                    <Icon className="h-4.5 w-4.5 h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-serif text-xl font-medium text-forest">{t}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-charcoal/65">{d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </Container>
      </section>

      <section className="noise-dark bg-forest py-28 text-cream sm:py-36">
        <Container className="relative z-10 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              index="03"
              eyebrow="Every stay includes"
              dark
              title="Everything handled, one invoice."
              copy="The same end-to-end operation we run for short-term guests, adapted for stays of 21 days or more."
            />
          </div>
          <div className="lg:col-span-7">
            <div className="grid gap-x-10 gap-y-5 sm:grid-cols-2">
              {INCLUDED.map((t, i) => (
                <FadeUp key={t} delay={i * 0.05}>
                  <div className="flex items-start gap-3" data-testid={`corporate-included-${i}`}>
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sage" />
                    <p className="text-sm leading-relaxed text-cream/75">{t}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <section id="corporate-enquiry" className="scroll-mt-24 bg-cream py-28 sm:py-36">
        <Container className="grid items-start gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <MaskedLines
              lineClassName="font-serif text-4xl sm:text-5xl font-medium leading-[1.1] text-forest"
              lines={[
                <span key="a">Tell us what</span>,
                <span key="b" className="italic text-forest-700">your team needs.</span>,
              ]}
            />
            <FadeUp delay={0.3}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-charcoal/70">
                Booking for a company? Choose "Corporate stay enquiry" below and describe your dates, headcount and
                preferred locations in the message. Own a property suited to corporate stays? The same form starts
                that conversation.
              </p>
            </FadeUp>
            <FadeUp delay={0.4}>
              <div className="mt-9 rounded-2xl border border-forest/12 bg-bone p-7">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal/45">Prefer to talk?</p>
                <p className="mt-3 text-sm font-medium text-charcoal" data-testid="corporate-contact-email">stay@fieldgreenproperty.com</p>
                <p className="mt-1 text-sm font-medium text-charcoal" data-testid="corporate-contact-phone">+353 85 736 2033 — Andrew</p>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.2} className="lg:col-span-6">
            <LeadForm
              source="corporate-stays"
              defaultEnquiry="corporate"
              submitLabel="Send Corporate Enquiry"
              note="Tell us your dates and headcount — we'll reply within one working day."
            />
          </FadeUp>
        </Container>
      </section>
    </main>
  );
}
